/* eslint-disable array-bracket-newline */
import { scan, filter, concatMap } from 'rxjs/operators'
import { KeyPressEvents } from './serialize'

const SHORTCUT_PLATFORM = {
  MAC: 'MAC',
  DEFAULT: 'DEFAULT',
}

const KEYS = {
  META: 'Meta',
  CTRL: 'Control',
  SHIFT: 'Shift',
  INSERT: 'Insert',
  V: 'v',
}

const STATES = {
  PASS: 'PASS',
  START: 'START',
  TRIGGER: 'TRIGGER',
  END_META: 'END_META',
  END_TRIGGER: 'END_TRIGGER',
}

const STATE_SHORTCUT_KEYS = {
  [SHORTCUT_PLATFORM.MAC]: {
    [STATES.START]: [{
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.META,
      metaKey: true,
    }],
    [STATES.TRIGGER]: [{
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      metaKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      metaKey: true,
      repeat: true,
    }],
    [STATES.END_META]: [{
      action: KeyPressEvents.KEY_UP,
      key: KEYS.META,
    }],
    [STATES.END_TRIGGER]: [{
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      repeat: true,
    }, {
      action: KeyPressEvents.KEY_UP,
      key: KEYS.V,
    }],
  },
  [SHORTCUT_PLATFORM.OTHER]: {
    [STATES.START]: [{
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.CTRL,
      ctrlKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.SHIFT,
      shiftKey: true,
    }],
    [STATES.TRIGGER]: [{
      action: KeyPressEvents.KEY_UP,
      key: KEYS.V,
      ctrlKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      ctrlKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      ctrlKey: true,
      repeat: true,
    }, {
      action: KeyPressEvents.KEY_UP,
      key: KEYS.INSERT,
      shiftKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.INSERT,
      shiftKey: true,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.INSERT,
      shiftKey: true,
      repeat: true,
    }],
    [STATES.END_META]: [{
      action: KeyPressEvents.KEY_UP,
      key: KEYS.CTRL,
    }, {
      action: KeyPressEvents.KEY_UP,
      key: KEYS.SHIFT,
    }],
    [STATES.END_TRIGGER]: [{
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.V,
      repeat: true,
    }, {
      action: KeyPressEvents.KEY_UP,
      key: KEYS.V,
    }, {
      action: KeyPressEvents.KEY_DOWN,
      key: KEYS.INSERT,
      repeat: true,
    }, {
      action: KeyPressEvents.KEY_UP,
      key: KEYS.INSERT,
    }],
  },
}

const currentShortcutPlatform = (() => {
  if (navigator && navigator.platform && navigator.platform.startsWith('Mac')) {
    return SHORTCUT_PLATFORM.MAC
  }
  return SHORTCUT_PLATFORM.OTHER
})()

const getStateShortcutKeys = (state) => STATE_SHORTCUT_KEYS[currentShortcutPlatform][state]

const eventMatches = (event = {}, oneOf = [], enabled) => oneOf.some(({
  key,
  action,
  repeat = false,
  metaKey = false,
  shiftKey = false,
  ctrlKey = false,
}) => (
  enabled &&
  event.key === key &&
  event.action === action &&
  event.repeat === repeat &&
  event.metaKey === metaKey &&
  event.shiftKey === shiftKey &&
  event.ctrlKey === ctrlKey
))

const evaluateStatePass = ({ event, enabled }) => {
  if (eventMatches(event, getStateShortcutKeys(STATES.START), enabled)) {
    return {
      state: STATES.START,
      events: [event],
      enabled,
    }
  }

  return {
    state: STATES.PASS,
    events: [event],
    enabled,
  }
}

const evaluateStateStart = ({ event, events, enabled }) => {
  if (eventMatches(event, getStateShortcutKeys(STATES.TRIGGER), enabled)) {
    return {
      state: STATES.TRIGGER,
      events: events.concat(event),
      enabled,
    }
  }

  return {
    state: STATES.PASS,
    events: events.concat(event),
    enabled,
  }
}

const evaluateStateTrigger = ({ event, events, enabled }) => {
  if (eventMatches(event, getStateShortcutKeys(STATES.TRIGGER), enabled)) {
    return {
      state: STATES.TRIGGER,
      events: events.concat(event),
      enabled,
    }
  }

  if (eventMatches(event, getStateShortcutKeys(STATES.END_META), enabled)) {
    return {
      state: STATES.END_META,
      events: events.concat(event),
      enabled,
    }
  }

  return {
    state: STATES.PASS,
    events: events.concat(event),
    enabled,
  }
}

const evaluateStateEndMeta = ({ event, events, enabled }) => {
  if (eventMatches(event, getStateShortcutKeys(STATES.END_TRIGGER), enabled)) {
    return {
      state: STATES.END_TRIGGER,
      events: events.concat(event),
      enabled,
    }
  }

  if (eventMatches(event, getStateShortcutKeys(STATES.START), enabled)) {
    return evaluateStatePass({ state: STATES.PASS, event, events: [event], enabled })
  }

  return {
    state: STATES.PASS,
    events: [event],
    enabled,
  }
}

const evaluateStateEndTrigger = ({ event, events, enabled }) => {
  if (eventMatches(event, getStateShortcutKeys(STATES.END_TRIGGER), enabled)) {
    return {
      state: STATES.END_TRIGGER,
      events: events.concat(event),
      enabled,
    }
  }

  if (eventMatches(event, getStateShortcutKeys(STATES.START), enabled)) {
    return evaluateStatePass({ state: STATES.PASS, event, events: [event], enabled })
  }

  return {
    state: STATES.PASS,
    events: [event],
    enabled,
  }
}

const evaluators = {
  [STATES.PASS]: evaluateStatePass,
  [STATES.START]: evaluateStateStart,
  [STATES.TRIGGER]: evaluateStateTrigger,
  [STATES.END_META]: evaluateStateEndMeta,
  [STATES.END_TRIGGER]: evaluateStateEndTrigger,
}

export function filterPasteShortcuts$ (keyboard$, state$) {
  const shortcutFilter$ = keyboard$.pipe(
    scan(({
      state,
      events,
      enabled,
    },
    event
    ) => evaluators[state]({ state, event, events, enabled }), {
      state: STATES.PASS,
      events: [],
      enabled: state$.value.clipboard.enabled,
    }),
    filter(({ state }) => state === STATES.PASS),
    concatMap(({ events }) => events),
  )

  return shortcutFilter$
}

export function shouldPreventDefaultForKeyDown ({ key, ctrlKey, metaKey, shiftKey }, { clipboard: { enabled } = {} } = {}) {
  return !(
    enabled && (
      (key === KEYS.V && ctrlKey) ||
      (key === KEYS.V && metaKey) ||
      (key === KEYS.INSERT && shiftKey)
    )
  )
}
